import { getMediaURL } from '../../context/API';
export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationRules {
  [key: string]: {
    validate: (value: any) => boolean;
    message: string;
  }[];
}
export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const phoneRegex = /^[\d\s\-()+.]+$/;
export const validateEmail = (email: string): boolean => {
  if (!email) return false;
  return emailRegex.test(email.trim());
};

export const validatePhone = (phone: string): boolean => {
  if (!phone) return false;
  const cleaned = phone.replace(/[\s\-().]/g, '');
  return cleaned.length >= 10 && phoneRegex.test(phone);
};

export const validateTaxRate = (tax: number): boolean => {
  return tax >= 0 && tax <= 100;
};

export const validateRequired = (value: string): boolean => {
  return value?.trim().length > 0;
};

export const validationRules: ValidationRules = {
  name: [
    {
      validate: (v: string) => validateRequired(v),
      message: 'Full name is required',
    },
    {
      validate: (v: string) => v?.trim().length >= 2,
      message: 'Name must be at least 2 characters',
    },
  ],
  username: [
    {
      validate: (v: string) => validateRequired(v),
      message: 'Username is required',
    },
  ],
  email: [
    {
      validate: (v: string) => validateRequired(v),
      message: 'Email is required',
    },
    {
      validate: (v: string) => validateEmail(v),
      message: 'Please enter a valid email address',
    },
  ],
  phone: [
    {
      validate: (v: string) => validateRequired(v),
      message: 'Phone number is required',
    },
    {
      validate: (v: string) => validatePhone(v),
      message: 'Please enter a valid phone number',
    },
  ],
  publicPhone: [
    {
      validate: (v: string) => !v || validatePhone(v),
      message: 'Please enter a valid phone number',
    },
  ],
  address: [
    {
      validate: (v: string) => validateRequired(v),
      message: 'Address is required',
    },
  ],
  tax: [
    {
      validate: (v: number) => validateTaxRate(v),
      message: 'Tax rate must be between 0 and 100',
    },
  ],
};

export const validateFormData = (
  formData: Record<string, any>,
  rules: ValidationRules,
  fieldsToValidate?: string[]
): ValidationError[] => {
  const errors: ValidationError[] = [];
  const rulesToCheck = fieldsToValidate
    ? Object.fromEntries(Object.entries(rules).filter(([k]) => fieldsToValidate.includes(k)))
    : rules;

  Object.entries(rulesToCheck).forEach(([field, fieldRules]) => {
    const value = formData[field];
    fieldRules.forEach((rule) => {
      if (!rule.validate(value)) {
        errors.push({ field, message: rule.message });
      }
    });
  });

  return errors;
};

export const getFieldError = (
  errors: ValidationError[],
  fieldName: string
): string | undefined => {
  return errors.find((e) => e.field === fieldName)?.message;
};

export const maskValue = (value: string): string => {
  if (!value || value.length < 4) return value || '—';
  return value.slice(0, 2) + '****' + value.slice(-2);
};


export const formatExpiresAt = (expiresAt: string | undefined | null): string => {
  if (!expiresAt) return '';
  try {
    const date = new Date(expiresAt);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffSecs = Math.floor((diffMs % 60000) / 1000);

    // Format local time (e.g., "3:45 PM")
    const localTime = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });

    if (diffMins > 0) {
      return `Expires at ${localTime} (${diffMins}m ${diffSecs}s)`;
    } else if (diffSecs > 0) {
      return `Expires at ${localTime} (${diffSecs}s)`;
    } else {
      return 'Code expired';
    }
  } catch (e) {
    return '';
  }
};

export const parseLoyaltyRatio = (value: string): number | undefined => {
  if (value === undefined || value === null || String(value).trim() === '') return undefined;
  const num = Number(String(value).trim());
  return Number.isFinite(num) ? num : undefined;
};

export const ROLE_CONFIG = {
  merchant: { color: 'blue', label: 'Merchant' },
  merchant_manager: { color: 'green', label: 'Manager' },
  merchant_staff: { color: 'cyan', label: 'Staff' },
  admin: { color: 'purple', label: 'Administrator' },
} as const;

export const getLogoUrl = (user: any): string | null => {
  if (!user) return null;

  const userAny = user as any;
  const logo = userAny.logo;

  if (!logo || logo === null || logo === 'null' || logo === '{}' || logo === '%7B%7D') {
    return null;
  }

  const logoString = typeof logo === 'string' ? logo.trim() : String(logo);
  if (logoString === '' || logoString === 'null' || logoString === '{}') {
    return null;
  }

  if (logoString.startsWith('http://') || logoString.startsWith('https://')) {
    return logoString;
  }

  return getMediaURL(logoString);
};
